import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { useNavigate } from 'react-router-dom';
import notificationService from '../../utils/notificationService.js';

// Cache keys for localStorage
const NOTIFICATIONS_CACHE_KEY = 'notifications_cache';
const NOTIFICATIONS_CACHE_TIMESTAMP = 'notifications_cache_timestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache validity

const NotificationBell = () => {
  // Initialize from cache for instant UI
  const getCachedNotifications = () => {
    try {
      const cached = localStorage.getItem(NOTIFICATIONS_CACHE_KEY);
      const timestamp = localStorage.getItem(NOTIFICATIONS_CACHE_TIMESTAMP);
      if (cached && timestamp) {
        const isValid = Date.now() - parseInt(timestamp) < CACHE_DURATION;
        if (isValid) {
          return JSON.parse(cached);
        } else {
          // Clear expired cache
          localStorage.removeItem(NOTIFICATIONS_CACHE_KEY);
          localStorage.removeItem(NOTIFICATIONS_CACHE_TIMESTAMP);
        }
      }
    } catch (e) {
      console.warn('Failed to read notification cache:', e);
    }
    return [];
  };

  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState(getCachedNotifications);
  const [unreadCount, setUnreadCount] = useState(() => {
    const cached = getCachedNotifications();
    // Only count truly unread items, default to 0 if cache is empty/invalid
    if (!cached || cached.length === 0) return 0;
    return cached.filter(n => !n.read && !n.read_at).length;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Faster polling interval (15 seconds for better responsiveness)
  const POLLING_INTERVAL = 15000;

  // Save to cache helper
  const saveToCache = useCallback((notificationList) => {
    try {
      localStorage.setItem(NOTIFICATIONS_CACHE_KEY, JSON.stringify(notificationList));
      localStorage.setItem(NOTIFICATIONS_CACHE_TIMESTAMP, Date.now().toString());
    } catch (e) {
      console.warn('Failed to save notification cache:', e);
    }
  }, []);

  // Initialize notifications and set up real-time updates
  useEffect(() => {
    loadNotifications();
    refreshUnreadCount();

    // Set up polling with faster interval
    const pollInterval = setInterval(() => {
      loadNotifications(true); // silent update
    }, POLLING_INTERVAL);

    // Initial check for first-time user (welcome notification)
    const checkWelcome = async () => {
      const isFirstTime = await notificationService.checkFirstTimeUser();
      if (isFirstTime) {
         // This logic is also in RoleBasedDashboard, but good to have here as backup
      }
    };
    checkWelcome();

    return () => {
      clearInterval(pollInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load notifications from service
  const loadNotifications = async (silent = false) => {
    if (!silent) setIsLoading(true);
    if (silent) setIsRefreshing(true);

    try {
      // Use service which handles fallbacks (mock data) if API fails
      const result = await notificationService.getNotifications({ limit: 50 });
      
      if (result.success) {
        const newItems = result.data || [];
        
        setNotifications(prevNotifications => {
          // Preserve local read state for items that were marked read optimistically
          return newItems.map(newItem => {
            const existing = prevNotifications.find(n => n.id === newItem.id);
            if (existing && existing.read && !newItem.read) {
              return { ...newItem, read: true, read_at: existing.read_at };
            }
            return newItem;
          });
        });
        
        // Calculate and update unread count
        const mergedItems = newItems.map(newItem => {
           const existing = notifications.find(n => n.id === newItem.id);
           if (existing && existing.read && !newItem.read) {
             return { ...newItem, read: true };
           }
           return newItem;
        });
        updateUnreadCount(mergedItems);
        
        // Save to cache for faster next load
        saveToCache(mergedItems);
      } else {
        console.warn('Failed to load notifications via service:', result.error);
        if (!silent) {
           setNotifications([]);
           updateUnreadCount([]);
        }
      }
    } catch (err) {
      console.error('Unexpected error loading notifications:', err);
      if (!silent) setNotifications([]); 
    } finally {
      if (!silent) setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const refreshUnreadCount = async () => {
    try {
      // Service method might need to be robust; checks real API or calculates from local
      // For now, calculating from fetched list is often safer if API is shaky, 
      // but let's try the service stats if available, else derive.
      const result = await notificationService.getNotificationStatistics();
      if (result.success && result.data?.unread_notifications !== undefined) {
         setUnreadCount(result.data.unread_notifications);
      } else {
         // Fallback: use local list count
         // This runs if fetching list was successful
      }
    } catch (e) {
      // ignore
    }
  };

  // Update unread count local helper
  const updateUnreadCount = (notificationList) => {
    const unread = notificationList.filter(n => !n.read).length;
    setUnreadCount(unread);
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read
      if (!notification.read) {
        await markAsRead(notification.id);
      }

      // Navigate
      if (notification.action_url) {
        navigate(notification.action_url);
      } else if (notification.type === 'task') {
        navigate('/kanban-board');
      } else if (notification.type === 'project') {
        navigate('/project-management');
      } else if (notification.type === 'team') {
        navigate('/team-members');
      }

      setIsOpen(false);
    } catch (error) {
      console.error('Failed to handle notification click:', error);
    }
  };

  // Mark as read
  const markAsRead = async (notificationId) => {
    // Optimistic update
    setNotifications(prev => {
      const updated = prev.map(n =>
        n.id === notificationId
          ? { ...n, read: true, read_at: new Date().toISOString() }
          : n
      );
      updateUnreadCount(updated);
      return updated;
    });

    try {
      await notificationService.markNotificationAsRead(notificationId);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      // Revert if needed? Usually not worth the UX jitter for ready status
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    // Optimistic update
    const updatedNotifications = notifications.map(n => ({
      ...n,
      read: true,
      read_at: new Date().toISOString()
    }));
    setNotifications(updatedNotifications);
    setUnreadCount(0);

    try {
      await notificationService.markAllNotificationsAsRead();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId, event) => {
    event.stopPropagation();
    
    // Optimistic update
    setNotifications(prev => {
      const updated = prev.filter(n => n.id !== notificationId);
      updateUnreadCount(updated);
      return updated;
    });

    try {
      await notificationService.deleteNotification(notificationId);
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  // Format notification time
  const formatNotificationTime = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    const now = Date.now();
    const notificationTime = new Date(timestamp).getTime();
    const diffMs = now - notificationTime;
    
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  // Get notification type icon
  const getNotificationTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'task': return 'CheckSquare';
      case 'project': return 'Folder';
      case 'team': return 'Users';
      case 'system': return 'Settings';
      case 'welcome': return 'Star';
      default: return 'Bell';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
      case 'urgent': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-blue-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className='relative' ref={dropdownRef}>
      {/* Notification Bell Button */}
      <Button
        variant='ghost'
        size='icon'
        onClick={() => {
          const next = !isOpen;
          setIsOpen(next);
          if (next) {
            loadNotifications(); // Refresh on open
          }
        }}
        className='relative'
      >
        <Icon name='Bell' size={18} />
        {unreadCount > 0 && (
          <span className='absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center'>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className='absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg z-50'>
          {/* Header */}
          <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700'>
            <div className='flex items-center gap-2'>
              <h3 className='text-sm font-semibold text-gray-900 dark:text-slate-100'>
                Notifications
              </h3>
              {isRefreshing && (
                <Icon name='RefreshCw' size={12} className='animate-spin text-gray-400' />
              )}
            </div>
            <div className='flex items-center gap-2'>
              {unreadCount > 0 && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={markAllAsRead}
                  className='text-xs'
                  disabled={isLoading}
                >
                  Mark all read
                </Button>
              )}
              <Button
                variant='ghost'
                size='icon'
                onClick={() => loadNotifications()}
                className='h-6 w-6'
                disabled={isLoading || isRefreshing}
                title='Refresh notifications'
              >
                <Icon name='RefreshCw' size={12} className={isLoading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>

          {/* Notifications List */}
          <div className='max-h-96 overflow-y-auto'>
            {isLoading && notifications.length === 0 ? (
              <div className='p-4 text-center text-gray-500'>
                <Icon
                  name='Loader2'
                  size={20}
                  className='animate-spin mx-auto mb-2'
                />
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div className='p-4 text-center text-gray-500'>
                <Icon
                  name='Bell'
                  size={24}
                  className='mx-auto mb-2 opacity-50'
                />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 border-b border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors cursor-pointer ${
                    !notification.read
                      ? 'bg-blue-50 border-l-4 border-l-blue-500'
                      : ''
                  }`}
                >
                  <div className='flex items-start space-x-3'>
                    <div className={`flex-shrink-0 ${getPriorityColor(notification.priority)}`}>
                      <Icon
                        name={getNotificationTypeIcon(notification.type)}
                        size={18}
                      />
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-start justify-between mb-2'>
                        <div className='flex-1'>
                          <div className='flex items-center gap-2 mb-1'>
                            <p className='text-sm font-semibold text-gray-900 dark:text-slate-100'>
                              {notification.title}
                            </p>
                            {!notification.read && (
                              <div className='w-2 h-2 bg-blue-500 rounded-full'></div>
                            )}
                          </div>
                          <p className='text-sm text-gray-600 dark:text-slate-300 mb-2'>
                            {notification.message}
                          </p>
                          <p className='text-xs text-gray-500 dark:text-slate-400'>
                            {formatNotificationTime(notification.created_at || notification.timestamp || notification.createdAt)}
                          </p>
                        </div>
                        <div className='flex items-center space-x-1 ml-2'>
                          {!notification.read && (
                            <Button
                              variant='ghost'
                              size='icon'
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              className='h-6 w-6 hover:bg-green-100 hover:text-green-600'
                              title='Mark as read'
                            >
                              <Icon name='Check' size={12} />
                            </Button>
                          )}
                          <Button
                            variant='ghost'
                            size='icon'
                            onClick={(e) => deleteNotification(notification.id, e)}
                            className='h-6 w-6 text-red-500 hover:text-red-600 hover:bg-red-100'
                            title='Delete notification'
                          >
                            <Icon name='X' size={12} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className='p-3 border-t border-gray-200 dark:border-slate-700'>
              <Button
                variant='ghost'
                size='sm'
                className='w-full text-xs'
                onClick={() => {
                  setIsOpen(false);
                  navigate('/notifications');
                }}
              >
                View all notifications
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
